import { useState, useRef, useCallback, type Dispatch, type SetStateAction } from 'react';
import type { Message } from '@/types/message';
import { apiService } from '@/services/api';

// Carga inicial: backend tem teto de 100 (le=100 em backend/routers/messages.py).
// Se a sala tiver mais, o usuário pede via "carregar antigas" (paginação reversa).
const INITIAL_PAGE_SIZE = 100;
// Página de "load more" (mensagens antigas via gesto do usuário).
const MESSAGES_PER_PAGE = 20;

// Ordena por horário (crescente), desempatando por id. O desempate por id é
// essencial: como o cliente reordena tudo, um sort só por timestamp deixaria
// mensagens do MESMO instante na ordem de chegada (que difere entre PC e
// celular). Comparando o id, todas as sessões chegam à mesma ordem. O backend
// aplica o mesmo desempate (order_by sent_at, id) na consulta.
function sortByTime(messages: Message[]): Message[] {
  return [...messages].sort((a, b) => {
    const ta = new Date(a.timestamp).getTime();
    const tb = new Date(b.timestamp).getTime();
    if (ta !== tb) return ta - tb;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

interface UseMessageHistoryProps {
  tableId: string;
  token?: string;
}

/**
 * Dono da lista de mensagens e de como ela é populada via REST: carga inicial
 * e paginação reversa. Extraído do useGameWebSocket (que era um "god hook") —
 * o useGameWebSocket continua aplicando os deltas de tempo real via setMessages.
 */
export function useMessageHistory({ tableId, token }: UseMessageHistoryProps) {
  const [messages, setMessagesRaw] = useState<Message[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const historyLoadedRef = useRef(false);
  // Rastreia o ID da mensagem mais antiga já usada como cursor (evita refetch)
  const oldestLoadedMessageIdRef = useRef<string | null>(null);
  // Filtro por missão ativa. undefined = sem filtro (comportamento atual, I2 —
  // mesa sem missão carrega tudo). Uma string filtra o chat àquela missão.
  const missionFilterRef = useRef<string | undefined>(undefined);
  const getMissionFilter = useCallback(() => missionFilterRef.current, []);

  // setMessages que mantém o array SEMPRE ordenado por horário. Antes as
  // mensagens ficavam na ordem de CHEGADA (inserção), então PC e celular —
  // sessões independentes com timings de rede diferentes — acabavam com
  // ordens distintas. Centralizando aqui, todo ponto de escrita (carga
  // inicial, paginação, mensagem nova, otimista) fica ordenado de uma vez.
  const setMessages = useCallback<Dispatch<SetStateAction<Message[]>>>((update) => {
    setMessagesRaw(prev => {
      const next = typeof update === 'function'
        ? (update as (p: Message[]) => Message[])(prev)
        : update;
      return sortByTime(next);
    });
  }, []);

  const loadMessageHistory = useCallback(async () => {
    if (!tableId || !token || historyLoadedRef.current) return;

    try {
      setLoadingHistory(true);
      const response = await apiService.getTableMessages(tableId, {
        limit: INITIAL_PAGE_SIZE,
        missionId: missionFilterRef.current,
      });
      setMessages(response.messages);
      setHasMoreMessages(response.has_more);
      historyLoadedRef.current = true;
    } catch (error) {
      console.error('[WebSocket] Erro ao carregar histórico:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [tableId, token]);

  // Recarrega o histórico filtrando por uma missão (ou sem filtro se undefined).
  // Chamado quando a missão ativa muda (ativação/encerramento). Reseta os
  // controles de paginação e substitui a lista pela da nova missão.
  const reloadHistory = useCallback(async (missionId?: string) => {
    if (!tableId || !token) return;
    if (missionFilterRef.current === missionId && historyLoadedRef.current) return;
    missionFilterRef.current = missionId;
    oldestLoadedMessageIdRef.current = null;
    setHasMoreMessages(true);
    try {
      setLoadingHistory(true);
      const response = await apiService.getTableMessages(tableId, {
        limit: INITIAL_PAGE_SIZE,
        missionId,
      });
      setMessages(response.messages);
      setHasMoreMessages(response.has_more);
      historyLoadedRef.current = true;
    } catch (error) {
      console.error('[WebSocket] Erro ao recarregar histórico por missão:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [tableId, token]);

  const loadMoreMessages = useCallback(async () => {
    if (!tableId || !token || loadingMoreMessages || !hasMoreMessages) return;

    const oldestMessage = messages[0];
    if (!oldestMessage) return;

    // Evitar carregar com o mesmo beforeId (duplicatas)
    if (oldestLoadedMessageIdRef.current === oldestMessage.id) return;

    try {
      setLoadingMoreMessages(true);
      oldestLoadedMessageIdRef.current = oldestMessage.id;

      const response = await apiService.getTableMessages(tableId, {
        limit: MESSAGES_PER_PAGE,
        beforeId: oldestMessage.id,
        missionId: missionFilterRef.current,
      });

      if (response.messages.length > 0) {
        // Filtrar mensagens que já existem para evitar duplicatas
        const existingIds = new Set(messages.map(m => m.id));
        const newMessages = response.messages.filter(m => !existingIds.has(m.id));

        if (newMessages.length > 0) {
          setMessages(prev => [...newMessages, ...prev]);
        }
      }
      setHasMoreMessages(response.has_more);
    } catch (error) {
      console.error('[WebSocket] Erro ao carregar mais mensagens:', error);
      // Resetar ref em caso de erro para permitir retry
      oldestLoadedMessageIdRef.current = null;
    } finally {
      setLoadingMoreMessages(false);
    }
  }, [tableId, token, loadingMoreMessages, hasMoreMessages, messages]);

  return {
    messages,
    setMessages,
    loadingHistory,
    loadingMoreMessages,
    hasMoreMessages,
    loadMessageHistory,
    loadMoreMessages,
    reloadHistory,
    getMissionFilter,
  };
}
